import { useCallback, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateTask, type Task, type TaskStatus } from '../api';
import { STATUS_VERB } from '../taskVocab';
import type { ReadbackClaim } from './TaskStatusControl';

/** One line's worth of "what the last press did", as `CommandReadback` draws it. */
interface ReadbackLine {
  verb: string;
  titles: string[];
  /** Where a status command took the rows; `null` for a bulk edit. Names the way back. */
  landed: TaskStatus | null;
  /** What each row was before, which is all the way back restores. Empty: no way back offered. */
  from: { id: string; status: TaskStatus }[];
  /** The server refused it — set by `withdraw`, see `CommandReadback`. */
  failed?: boolean;
  /**
   * Rows of this line the server refused while the rest saved — set by `withdraw` with ids.
   *
   * Their titles, taken out of `titles` and their rows out of `from`, so the sentence names what
   * did happen, the way back restores only that, and this names what did not.
   */
  refused?: string[];
  /** Which press put this line up, so only that press's refusal can take it down. */
  claim?: number;
}

/**
 * The state behind one screen's readback: the line, the press that put it up, and the way back.
 *
 * It was written three times — `lastCommand` in `TaskList`, `lastStart` on Today, nothing on the
 * Board — and the copies had already drifted: Today's could only say *Started*, so ticking a box
 * there completed the task, removed the row and said nothing (`01a03813-a118`, the minimalist's
 * *call the bank tomorrow*). One hook so every screen that changes a status records it the same
 * way, with the same claim rule and the same way back.
 *
 * `record` is called **before** the request (the row controls call it from `onMutate`), so it
 * snapshots `before` from the caller's copy of the rows; `withdraw` turns the line into the refusal
 * when that request fails. `goBack` writes each row's old status — status alone, which is why the
 * line's button is *Reopen* or *Put back* and never *Undo* (see the note on `CommandReadback`).
 */
export function useCommandReadback() {
  const queryClient = useQueryClient();
  const [line, setLine] = useState<ReadbackLine | null>(null);
  /** Hands out `claim`s; a ref because a claim has to be known before the state it lands in is. */
  const claims = useRef(0);

  const record = useCallback(
    (
      status: TaskStatus,
      before: readonly Pick<Task, 'id' | 'title' | 'status'>[],
    ): ReadbackClaim => {
      if (before.length === 0) return undefined;
      const claim = ++claims.current;
      setLine({
        claim,
        /*
          The state it landed in, from `STATUS_VERB` — not the button's label lower-cased. A
          button says what it does and a readback says what happened.
        */
        verb: STATUS_VERB[status].past,
        landed: status,
        titles: before.map((t) => t.title),
        from: before.map((t) => ({ id: t.id, status: t.status })),
      });
      return claim;
    },
    [],
  );

  /*
    Only while it is still the line this press put up. A later command may have replaced it, and
    that one's claim is not this failure's to take down — matched on the press, not the row, since
    a later command can name the same row with the same status (`01a16009-e4e1`).
  */
  /*
    And only the rows that were refused, when the press was several requests and the caller knows
    which one failed (`ids`). The Board moves a batch as one request per card under one claim, and
    a whole-line withdraw on one card's refusal told the reader that a card they could no longer see
    had not been completed — and took away the only Reopen for it (MR !104, round 1: Alpha saved
    and left the column, Bravo was refused, and the line read "2 tasks were not completed"). With
    ids, the refused rows leave `titles` and `from` for `refused`: the sentence keeps what saved,
    its way back restores only that, and a second clause names what did not. Once nothing saved,
    it is the whole-line refusal again, naming every row.

    Without ids, the whole line — right for a single request, which is all or nothing.
  */
  const withdraw = useCallback((claim: ReadbackClaim, ids?: readonly string[]) => {
    if (claim === undefined) return;
    setLine((l) => {
      if (l?.claim !== claim) return l;
      if (ids === undefined) return { ...l, failed: true };
      const gone = new Set(ids);
      const keep = l.from.flatMap((f, i) => (gone.has(f.id) ? [] : [i]));
      const lost = l.from.flatMap((f, i) => (gone.has(f.id) ? [i] : []));
      if (lost.length === 0) return l;
      const refused = [...(l.refused ?? []), ...lost.map((i) => l.titles[i] ?? '')];
      if (keep.length === 0) return { ...l, titles: refused, from: [], refused: [], failed: true };
      return {
        ...l,
        titles: keep.map((i) => l.titles[i] ?? ''),
        from: keep.map((i) => l.from[i]!),
        refused,
      };
    });
  }, []);

  const goBack = useMutation({
    mutationFn: async (from: ReadbackLine['from']) => {
      for (const t of from) await updateTask(t.id, { status: t.status });
    },
    onSuccess: () => {
      setLine(null);
      void queryClient.invalidateQueries();
    },
  });

  return { line, show: setLine, record, withdraw, goBack };
}

/**
 * The line drawn from `useCommandReadback`, in the slot every screen gives it.
 *
 * **Its own sticky slot under the header**, on Tasks, Today and the Board (`01a03813-a118`). It
 * was a static line above the first row: the manager, scrolled to row 25 of 61, pressed *To next*
 * and the only undo measured top -652 at scrollY 1076. Not a corner toast — the failed form below
 * keeps "the slot and the subject", and `QueryState` already records why this app keeps state out
 * of corners — and not inside the selection bar, which exists only while rows are selected, when
 * a tick or an `x` happens with nothing selected. Where a screen has a selection bar, the bar and
 * this line share one pinned dock (`.command-dock`) so they stack rather than overlap.
 */
export function ReadbackSlot({
  readback,
  tail,
}: {
  readback: ReturnType<typeof useCommandReadback>;
  tail?: string;
}) {
  const { line, goBack } = readback;
  if (line === null) return null;
  return (
    <CommandReadback
      verb={line.verb}
      titles={line.titles}
      landed={line.landed}
      tail={line.failed ? undefined : tail}
      canGoBack={line.from.length > 0}
      failed={line.failed}
      refused={line.refused}
      goingBack={goBack.isPending}
      onGoBack={() => goBack.mutate(line.from)}
      onDismiss={() => readback.show(null)}
    />
  );
}

/**
 * What the last press did, named, with the way back — one line, on Tasks, Today and the Board.
 *
 * It lived inline in `TaskList`, the only screen that had one, so a Start on Today changed a row,
 * moved it into another group and said nothing (`01a15a31-174b`: the ADHD persona's press "opened
 * nothing, showed nothing"). One component so the two screens cannot word the same act two ways.
 *
 * **The way back is named for the direction it goes.** *Reopen* after a command that landed in
 * done or dropped — completing runs the recurring successor, the habit entry and the timeline
 * block, and putting the status back reverses none of them, so "Undo" would overclaim (see
 * `recordCommand` in `TaskList`). After anything else it is *Put back*: "Reopen" after
 * *Started “X”.* names a direction the task never went.
 *
 * `role="status"`, as the WIP sentence is, so the announcement reaches a reader who cannot see the
 * row change — the bargain `TaskProgressMark`'s header makes about the mark itself.
 *
 * **And when the press did not happen, it says that instead** (`01a15e3c-61e3`). The line goes up
 * before the request, because the row can move on a refetch while the request is in flight (see
 * `onMutate` in `TaskStatusControl`); a refusal therefore has to replace it rather than merely
 * never arrive. The failed line keeps the slot and the subject — the reader looking where *Started
 * “X”.* just appeared finds the correction in the same place — and drops the way back, the tail
 * and the past tense, all three of which describe a change that is not there. `role="alert"`
 * there, because it contradicts something a screen reader has already announced.
 */
function CommandReadback({
  verb,
  titles,
  landed,
  tail,
  canGoBack,
  goingBack,
  onGoBack,
  onDismiss,
  failed = false,
  refused = [],
}: {
  /** The state it landed in, as `STATUS_VERB[status].past` says it. */
  verb: string;
  titles: readonly string[];
  /** Where the command took the rows, which decides what the way back is called. */
  landed: TaskStatus | null;
  /** A clause after the subject, before the full stop — Tasks' "— N in progress". */
  tail?: string;
  /** Only when there is something it can genuinely put back; a bulk edit gets no button. */
  canGoBack: boolean;
  goingBack: boolean;
  onGoBack: () => void;
  onDismiss: () => void;
  /** The server refused the command this line announced. */
  failed?: boolean;
  /** Titles of the rows refused while the rest saved — see `withdraw`. */
  refused?: readonly string[];
}) {
  if (titles.length === 0) return null;
  const name = (ts: readonly string[]) => (ts.length === 1 ? `“${ts[0]}”` : `${ts.length} tasks`);
  /*
    The same, drawn: a lone title in its own box, so on a refusal — which wraps at 375 rather than
    clipping its last clause, see `.command-dock .command-readback[role='alert']` — a long title is
    what gives way and the line stays two or three rows rather than one per twenty characters.
  */
  const named = (ts: readonly string[]) =>
    ts.length === 1 ? (
      <>
        “<span className="command-readback-title">{ts[0]}</span>”
      </>
    ) : (
      `${ts.length} tasks`
    );
  const subject = name(titles);
  if (failed) {
    const said = `${subject} ${titles.length === 1 ? 'was' : 'were'} not ${verb.toLowerCase()} — the change did not save.`;
    return (
      <p
        className="subtext command-readback"
        data-testid="command-readback"
        data-failed="true"
        role="alert"
      >
        <span className="command-readback-said" title={said}>
          {named(titles)} {titles.length === 1 ? 'was' : 'were'} not {verb.toLowerCase()} — the
          change did not save.
        </span>{' '}
        <button type="button" className="link" onClick={onDismiss}>
          dismiss
        </button>
      </p>
    );
  }
  const back = landed === 'done' || landed === 'dropped' ? 'Reopen' : 'Put back';
  /*
    Part of the press saved and part did not. The saved part keeps its sentence and its way back;
    the refused part is a clause after it, in the failed line's words. An alert, as the failed line
    is, because it contradicts what was announced a moment ago — and no tail, which counted a
    change that did not fully happen.
  */
  /*
    At 375 this line used to clip like any other, and the clause that gave way was the refusal:
    `Completed 2 tasks; “Call the bank tomorro…  Reopen dismiss`, a success with a dangling clause
    (MR !104, round 2, measured in the CI image). So a refusal wraps — see the stylesheet — rather
    than leading with the refusal on one line, which would clip the saved half instead and leave a
    Reopen with nothing beside it saying what it reopens.
  */
  const partial = refused.length > 0;
  const refusal = `${refused.length === 1 ? 'was' : 'were'} not ${verb.toLowerCase()} — the change did not save.`;
  const said = partial
    ? `${verb} ${subject}; ${name(refused)} ${refusal}`
    : `${verb} ${subject}${tail ?? ''}.`;
  return (
    <p
      className="subtext command-readback"
      data-testid="command-readback"
      data-failed={partial ? 'partial' : undefined}
      role={partial ? 'alert' : 'status'}
    >
      {/* Its own box so a long title is what gives way when the slot is one line — see
          `.command-dock` in the stylesheet. */}
      <span className="command-readback-said" title={said}>
        {partial ? (
          <>
            {verb} {named(titles)}; {named(refused)} {refusal}
          </>
        ) : (
          said
        )}
      </span>{' '}
      {canGoBack && (
        <>
          <button type="button" className="link" disabled={goingBack} onClick={onGoBack}>
            {back}
          </button>{' '}
        </>
      )}
      <button type="button" className="link" onClick={onDismiss}>
        dismiss
      </button>
    </p>
  );
}
